/**
 * Swipe-to-mark-read wrapper for a notification card.
 *
 * Mobile: drag RIGHT → reveal teal/emerald "Mark as read" affordance →
 *   release past threshold (or fast flick) → markAsRead. This direction
 *   matches PAGES.md §3.72 ("Swipe-right to mark read") and iOS Mail's
 *   convention of reserving swipe-left for destructive actions.
 * Desktop (lg+): a tiny visible "Mark as read" button on the right of the
 *   card body provides the keyboard / mouse path (WCAG 2.5.1 requires that
 *   any path-based gesture have an alternative single-pointer activation).
 *
 * Tap/click on the body itself opens the notification via the parent's
 * `onClick` — exactly the same behavior as before this gesture was added.
 */

import { useEffect, useRef } from 'react';
import { useNotificationStore } from '@/stores/use-notification-store';
import type { Notification } from '@/services/notification.service';
import { useSwipeToAction, SwipeRevealBackground } from '@/hooks/use-swipe-to-action';
import { NotificationCardBody } from '@/components/common/NotificationCardBody';
import { cn } from '@/constants/design-tokens';

// ─── Icons ──────────────────────────────────────────────────────────────────

function CheckIcon({ className = 'h-7 w-7' }: { className?: string }) {
  // Same path family as CheckAllIcon in NotificationPanel.
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CheckSmallIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

// ─── Keyboard fallback button ───────────────────────────────────────────────
//
// Visible mini check button shown only on lg+ for unread cards. Mobile users
// use the swipe gesture; desktop users (mouse + keyboard) get this button.

interface MarkReadButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

function MarkReadButton({ onClick }: MarkReadButtonProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Stop the parent <button> (NotificationCardBody) from also firing.
    e.stopPropagation();
    onClick(e);
  };
  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    // Don't let the swipe wrapper hijack the pointer for this nested control.
    e.stopPropagation();
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      aria-label="Mark as read"
      title="Mark as read"
      className={cn(
        'hidden lg:inline-flex shrink-0 items-center justify-center w-11 h-11 ml-1 rounded-xl',
        'text-neutral-400 hover:text-emerald-600 dark:hover:text-emerald-300',
        'hover:bg-emerald-50 dark:hover:bg-emerald-900/30',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500',
        'transition-colors duration-150',
      )}
    >
      <CheckSmallIcon />
    </button>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export interface SwipeableNotificationCardProps {
  notification: Notification;
  onClick: () => void;
  /** Show the whole message (the full notifications page, T24). */
  fullMessage?: boolean;
  /** Overrides the store's markAsRead — a page with its own list keeps it in step (T24). */
  onMarkRead?: (id: string) => void;
}

export function SwipeableNotificationCard({
  notification,
  onClick,
  fullMessage,
  onMarkRead,
}: SwipeableNotificationCardProps) {
  const storeMarkAsRead = useNotificationStore((s) => s.markAsRead);
  const markAsRead = onMarkRead ?? storeMarkAsRead;
  const isUnread = !notification.is_read;
  const vibratedRef = useRef(false);

  const { bind, state, progress, totalDelta } = useSwipeToAction({
    onCommit: () => {
      void markAsRead(notification.id);
    },
    direction: 'right',
    disabled: !isUnread,
  });

  // Vibrate exactly once per gesture, on the first crossing of progress=1.
  useEffect(() => {
    if (state !== 'dragging') {
      vibratedRef.current = false;
      return;
    }
    if (progress >= 1 && !vibratedRef.current) {
      vibratedRef.current = true;
      try {
        navigator.vibrate?.(15);
      } catch {
        /* vibration unavailable — silently ignore */
      }
    }
  }, [progress, state]);

  // Tap pass-through: if the gesture stayed within 8px and never crossed
  // the axis lock, treat it as a click. The visual body's <button onClick>
  // already fires for mouse/keyboard activation; we only need to suppress
  // the gesture wrapper's swipe path from interfering. The native click
  // event will fire after pointerup, so the consumer's onClick runs
  // automatically — we don't need to call it manually here.
  //
  // What we DO need: prevent the click from firing if a real swipe just
  // committed (state === 'committed' || 'settling' with non-zero offset).
  const isMidGesture = state === 'dragging' || state === 'settling' || state === 'committed';
  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    // The native click runs after pointerup. If the user actually dragged
    // more than the tap tolerance OR a commit/settle is in progress, swallow
    // the click so the parent onClick doesn't open the notification.
    if (totalDelta > 8 || isMidGesture) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleMarkReadButton = () => {
    void markAsRead(notification.id);
  };

  // Read cards: render the body as-is with no wrapper or reveal layer.
  if (!isUnread) {
    return <NotificationCardBody notification={notification} onClick={onClick} fullMessage={fullMessage} />;
  }

  // Pointer events bind on the OUTER wrapper so that any descendant pointer
  // (the card body, the keyboard mini button) participates in the gesture.
  // The visual translate happens on an inner div so the reveal background
  // stays put behind it.
  return (
    <div
      ref={bind.ref as React.RefCallback<HTMLDivElement>}
      onPointerDown={bind.onPointerDown}
      onPointerMove={bind.onPointerMove}
      onPointerUp={bind.onPointerUp}
      onPointerCancel={bind.onPointerCancel}
      onClickCapture={handleClickCapture}
      className="relative rounded-2xl"
      style={{ touchAction: bind.style.touchAction }}
      aria-roledescription="Swipeable notification, swipe right to mark as read"
      data-swipe-state={state}
      data-testid="swipeable-notification-card"
    >
      <SwipeRevealBackground
        progress={progress}
        state={state}
        label="Release to mark read"
        icon={<CheckIcon className="h-7 w-7" />}
        side="left"
      />

      <div
        style={bind.style}
        className="relative z-10"
      >
        <NotificationCardBody
          notification={notification}
          onClick={onClick}
          trailingAccessory={<MarkReadButton onClick={handleMarkReadButton} />}
          fullMessage={fullMessage}
        />
      </div>
    </div>
  );
}
